import { useEffect, useRef, useState } from "react";
import { Bot, Loader2, Send, Sparkles, Trash2, Volume2, X } from "lucide-react";
import { toast } from "sonner";
import { API, api, speakText } from "../lib/api";
import { useShop } from "../context/ShopContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

const SUGGESTIONS = [
    "Antep fıstığında en uygun tedarikçi kim?",
    "Bu ay için 50 kg pul biber alımı öner",
    "Güven skoru en yüksek tedarikçiler hangileri?",
    "Kuru meyve fiyatlarını karşılaştır",
];

export default function AiDrawer() {
    const { aiOpen, setAiOpen } = useShop();
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState("");
    const [streaming, setStreaming] = useState(false);
    const [voices, setVoices] = useState([]);
    const [voiceConfigured, setVoiceConfigured] = useState(false);
    const [voiceId, setVoiceId] = useState("");
    const [speakingIdx, setSpeakingIdx] = useState(null);
    const scrollRef = useRef(null);

    useEffect(() => {
        if (aiOpen) {
            api.get("/ai/history").then((r) => setMessages(r.data)).catch(() => {});
            api.get("/tts/voices").then((r) => {
                setVoices(r.data.voices || []);
                setVoiceConfigured(!!r.data.configured);
                if (r.data.current) setVoiceId(r.data.current);
                else if (r.data.voices?.length) setVoiceId(r.data.voices[0].voice_id);
            }).catch(() => {});
        }
    }, [aiOpen]);

    const playVoice = async (content, idx) => {
        if (speakingIdx !== null) return;
        setSpeakingIdx(idx);
        try {
            const audio = await speakText(content, voiceId || undefined);
            audio.onended = () => setSpeakingIdx(null);
        } catch (e) {
            toast.error(e.message);
            setSpeakingIdx(null);
        }
    };

    const changeVoice = async (v) => {
        setVoiceId(v);
        try {
            await api.post("/tts/preference", { voice_id: v });
            toast.success("Ses tercihi kaydedildi");
        } catch { /* sessiz */ }
    };

    useEffect(() => {
        scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
    }, [messages]);

    const send = async (text) => {
        const msg = (text ?? input).trim();
        if (!msg || streaming) return;
        setInput("");
        setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
        setStreaming(true);
        try {
            const resp = await fetch(`${API}/ai/chat`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({ message: msg }),
            });
            if (!resp.ok) {
                let detail = "AI asistan şu anda kullanılamıyor.";
                try { detail = (await resp.json()).detail || detail; } catch { /* yoksay */ }
                setMessages((m) => {
                    const copy = [...m];
                    copy[copy.length - 1] = { role: "assistant", content: detail };
                    return copy;
                });
                return;
            }
            const reader = resp.body.getReader();
            const decoder = new TextDecoder();
            let buf = "";
            for (;;) {
                const { done, value } = await reader.read();
                if (done) break;
                buf += decoder.decode(value, { stream: true });
                const parts = buf.split("\n\n");
                buf = parts.pop();
                for (const part of parts) {
                    if (!part.startsWith("data: ")) continue;
                    const d = part.slice(6);
                    if (d === "[DONE]") break;
                    try {
                        const j = JSON.parse(d);
                        if (j.token) {
                            setMessages((m) => {
                                const copy = [...m];
                                copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + j.token };
                                return copy;
                            });
                        }
                        if (j.error) {
                            setMessages((m) => {
                                const copy = [...m];
                                copy[copy.length - 1] = { role: "assistant", content: "Bir hata oluştu: " + j.error };
                                return copy;
                            });
                        }
                    } catch { /* yoksay */ }
                }
            }
        } catch {
            setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: "Bağlantı hatası. Lütfen tekrar deneyin." };
                return copy;
            });
        } finally {
            setStreaming(false);
        }
    };

    const clear = async () => {
        await api.delete("/ai/history");
        setMessages([]);
    };

    if (!aiOpen) return null;

    return (
        <div className="fixed inset-0 z-50" data-testid="ai-drawer">
            <div className="absolute inset-0 bg-black/40 backdrop-blur-[2px]" onClick={() => setAiOpen(false)} />
            <div className="absolute right-0 top-0 flex h-full w-full flex-col border-l border-amber-900/30 bg-stone-950 text-stone-100 shadow-2xl sm:w-[450px]">
                <div className="flex items-center gap-3 border-b border-stone-800 p-4">
                    <div className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-terra-600 to-saffron-400">
                        <Sparkles className="h-4 w-4 text-white" />
                    </div>
                    <div className="flex-1">
                        <p className="font-display font-bold">Angora AI Asistan</p>
                        <p className="text-xs text-stone-400">Satın alma copilot'unuz — canlı platform verisiyle</p>
                    </div>
                    {voiceConfigured ? (
                        <select
                            value={voiceId}
                            onChange={(e) => changeVoice(e.target.value)}
                            className="h-8 max-w-32 rounded-md border border-stone-700 bg-stone-900 px-2 text-xs text-stone-200"
                            data-testid="ai-voice-select"
                        >
                            {voices.map((v) => <option key={v.voice_id} value={v.voice_id}>{v.name}</option>)}
                        </select>
                    ) : (
                        <span className="hidden text-[10px] text-stone-500 sm:block" data-testid="ai-voice-not-configured" title="ELEVENLABS_API_KEY tanımlanınca sesli yanıt aktif olur">Sesli yanıt: anahtar gerekli</span>
                    )}
                    <Button variant="ghost" size="icon" onClick={clear} className="text-stone-400 hover:text-white" data-testid="ai-clear-history">
                        <Trash2 className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => setAiOpen(false)} className="text-stone-400 hover:text-white" data-testid="ai-drawer-close">
                        <X className="h-5 w-5" />
                    </Button>
                </div>

                <div ref={scrollRef} className="flex-1 space-y-4 overflow-y-auto p-4 scrollbar-thin" data-testid="ai-messages">
                    {messages.length === 0 && (
                        <div className="mt-6 space-y-3">
                            <div className="flex justify-center"><Bot className="h-10 w-10 text-terra-500" /></div>
                            <p className="text-center text-sm text-stone-400">
                                Ürün arama, fiyat karşılaştırma, tedarikçi önerisi ve satın alma planlaması için sorun.
                            </p>
                            <div className="grid gap-2 pt-2">
                                {SUGGESTIONS.map((s) => (
                                    <button
                                        key={s}
                                        onClick={() => send(s)}
                                        className="rounded-lg border border-stone-800 bg-stone-900 px-3 py-2 text-left text-xs text-stone-300 transition-colors hover:border-terra-500/50 hover:text-white"
                                        data-testid={`ai-suggestion-${SUGGESTIONS.indexOf(s)}`}
                                    >
                                        {s}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                    {messages.map((m, i) => (
                        <div key={i} className={`flex flex-col ${m.role === "user" ? "items-end" : "items-start"}`}>
                            <div
                                className={`max-w-[85%] whitespace-pre-wrap rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                                    m.role === "user" ? "bg-terra-600 text-white" : "bg-stone-900 text-stone-200 border border-stone-800"
                                }`}
                                data-testid={`ai-message-${i}`}
                            >
                                {m.content || (streaming && i === messages.length - 1 ? <span className="inline-block h-4 w-2 animate-pulse bg-terra-500" /> : "")}
                            </div>
                            {m.role === "assistant" && m.content && (
                                <button
                                    onClick={() => playVoice(m.content, i)}
                                    className="mt-1 inline-flex items-center gap-1 text-[10px] font-medium text-stone-500 transition-colors hover:text-terra-500"
                                    data-testid={`ai-listen-${i}`}
                                >
                                    {speakingIdx === i ? <Loader2 className="h-3 w-3 animate-spin" /> : <Volume2 className="h-3 w-3" />} Dinle
                                </button>
                            )}
                        </div>
                    ))}
                </div>

                <div className="border-t border-stone-800 p-4">
                    <form
                        onSubmit={(e) => { e.preventDefault(); send(); }}
                        className="flex gap-2"
                    >
                        <Input
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            placeholder="Örn: 100 kg kırmızı mercimek için en iyi fiyat?"
                            className="border-stone-800 bg-stone-900 text-stone-100 placeholder:text-stone-500"
                            disabled={streaming}
                            data-testid="ai-chat-input"
                        />
                        <Button type="submit" disabled={streaming || !input.trim()} className="bg-terra-600 hover:bg-terra-700 text-white" data-testid="ai-chat-send">
                            <Send className="h-4 w-4" />
                        </Button>
                    </form>
                </div>
            </div>
        </div>
    );
}
